/**
 * PhaseTransitionDialog
 *
 * AlertDialog shown when a drag-drop triggers a phase transition that has
 * associated actions (create payment plan, legal procedure, etc.).
 * Also used for backward-move confirmation.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { IncassoPhase } from '../shared/incassoTypes';

interface PhaseTransitionDialogProps {
  isOpen: boolean;
  targetPhase: IncassoPhase;
  ticketLabel: string;
  /** If true, the transition is backward — confirmation only, no related action */
  isBackward?: boolean;
  onConfirm: (createRelated: boolean) => void;
  onCancel: () => void;
}

interface PhaseTransitionContent {
  title: string;
  description: string;
  confirmLabel: string;
  skipLabel: string;
}

const getTransitionContent = (
  phase: IncassoPhase,
  ticketLabel: string,
  isBackward: boolean,
): PhaseTransitionContent => {
  if (isBackward) {
    return {
      title: 'Naar een eerdere fase verplaatsen?',
      description: `U verplaatst "${ticketLabel}" naar een eerdere fase. Weet u zeker dat u wilt doorgaan?`,
      confirmLabel: 'Ja, verplaatsen',
      skipLabel: 'Annuleren',
    };
  }

  switch (phase) {
    case IncassoPhase.MINNELIJKE_REGELING:
      return {
        title: 'Afbetalingsplan aanmaken?',
        description: `Wilt u een afbetalingsplan aanmaken voor "${ticketLabel}"?`,
        confirmLabel: 'Ja, afbetalingsplan aanmaken',
        skipLabel: 'Nee, alleen fase wijzigen',
      };

    case IncassoPhase.JURIDISCHE_PROCEDURE_OP_TE_STARTEN:
      return {
        title: 'Juridische procedure aanmaken?',
        description: `Wilt u een juridische procedure aanmaken voor "${ticketLabel}"?`,
        confirmLabel: 'Ja, procedure aanmaken',
        skipLabel: 'Nee, alleen fase wijzigen',
      };

    default:
      return {
        title: 'Fase wijzigen?',
        description: `Weet u zeker dat u "${ticketLabel}" naar een andere fase wilt verplaatsen?`,
        confirmLabel: 'Ja, verplaatsen',
        skipLabel: 'Annuleren',
      };
  }
};

export const PhaseTransitionDialog = ({
  isOpen,
  targetPhase,
  ticketLabel,
  isBackward = false,
  onConfirm,
  onCancel,
}: PhaseTransitionDialogProps) => {
  const content = getTransitionContent(targetPhase, ticketLabel, isBackward);

  const handleConfirm = () => {
    try {
      onConfirm(true);
    } catch (error) {
      console.error('[PhaseTransitionDialog] onConfirm(true) failed:', error);
      throw error;
    }
  };

  const handleSkip = () => {
    try {
      // For backward moves, "skip" = cancel entirely
      if (isBackward) {
        onCancel();
      } else {
        onConfirm(false);
      }
    } catch (error) {
      console.error('[PhaseTransitionDialog] skip/cancel failed:', error);
      throw error;
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{content.title}</AlertDialogTitle>
          <AlertDialogDescription>{content.description}</AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleSkip}>
            {content.skipLabel}
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm}>
            {content.confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
